import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { MultiSelect } from '../components/ui/multi-select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import PromoFeaturedBanner, { PromoBadge } from '../components/PromoFeaturedBanner';
import { 
    Search, 
    ShoppingCart, 
    Package,
    BatteryFull,
    BatteryMedium,
    BatteryLow,
    AlertTriangle,
    ChevronUp,
    ChevronDown,
    Plus,
    Check,
    CheckCircle,
    Wrench,
    Smartphone,
    ChevronRight,
    RefreshCw,
    Lock,
    Unlock,
    HardDrive,
    Cloud,
    Filter,
    X,
    Tag,
    Flame,
    Clock,
    Sparkles,
    ChevronLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Filter out "Full Ok" from pannes as it's not a real defect
const filterPannes = (pannes) => {
    if (!pannes || pannes.length === 0) return [];
    return pannes.filter(p => p && p.toLowerCase() !== 'full ok');
};

const PannesDisplay = ({ pannes, productName }) => {
    const filteredPannes = filterPannes(pannes);
    if (!filteredPannes || filteredPannes.length === 0) {
        return <span className="text-emerald-600 text-xs font-semibold">Aucune</span>;
    }

    if (filteredPannes.length <= 2) {
        return (
            <div className="flex flex-col gap-0.5">
                {filteredPannes.map((panne, idx) => (
                    <span key={idx} className="text-amber-600 text-xs flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {panne}
                    </span>
                ))}
            </div>
        );
    }

    return (
        <Dialog>
            <DialogTrigger asChild>
                <button className="text-left hover:bg-amber-50 rounded-xl p-1 -m-1 transition-colors">
                    <div className="flex flex-col gap-0.5">
                        {filteredPannes.slice(0, 2).map((panne, idx) => (
                            <span key={idx} className="text-amber-600 text-xs flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                {panne}
                            </span>
                        ))}
                        <span className="text-amber-700 text-xs font-semibold flex items-center gap-1">
                            <ChevronRight className="w-3 h-3" />
                            +{filteredPannes.length - 2} autre(s)
                        </span>
                    </div>
                </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md bg-white rounded-[2rem] border-0 shadow-[0_20px_60px_-12px_rgba(124,58,237,0.25)]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                        Pannes - {productName}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-2 mt-4">
                    {filteredPannes.map((panne, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-4 bg-amber-50 rounded-2xl border border-amber-200">
                            <Wrench className="w-5 h-5 text-amber-600" />
                            <span className="text-amber-800 font-medium">{panne}</span>
                        </div>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
};

// Compact version for the new Utopya-style layout
const CompactPannesDisplay = ({ pannes, productName, hasNiveau3 }) => {
    const filteredPannes = filterPannes(pannes);

    // Badge Niveau 3 component
    const Niveau3Badge = () => hasNiveau3 ? (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded-full border border-red-200">
            <AlertTriangle className="w-2.5 h-2.5" />
            Niv.3
        </span>
    ) : null;

    if (!filteredPannes || filteredPannes.length === 0) {
        return (
            <div className="flex flex-col items-center gap-1">
                {hasNiveau3 && <Niveau3Badge />}
                <span className="text-emerald-600 text-xs font-medium flex items-center justify-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Aucune
                </span>
            </div>
        );
    }

    // Show pannes inline, more visible
    if (filteredPannes.length === 1) {
        return (
            <div className="flex flex-col items-center gap-1">
                {hasNiveau3 && <Niveau3Badge />}
                <span className="text-amber-600 text-xs font-medium flex items-center justify-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    {filteredPannes[0]}
                </span>
            </div>
        );
    }

    // Multiple pannes - show tooltip on hover
    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className="cursor-pointer hover:bg-amber-50 rounded-lg px-2 py-1 -m-1 transition-colors">
                        <div className="flex flex-col items-center gap-0.5">
                            {hasNiveau3 && <Niveau3Badge />}
                            <span className="text-amber-600 text-xs font-medium flex items-center gap-1">
                                <AlertTriangle className="w-3.5 h-3.5" />
                                {filteredPannes[0]}
                            </span>
                            <span className="text-amber-700 text-[10px] font-semibold">
                                +{filteredPannes.length - 1} autre{filteredPannes.length > 2 ? 's' : ''}
                            </span>
                        </div>
                    </div>
                </TooltipTrigger>
                <TooltipContent 
                    side="bottom" 
                    className="bg-white border border-amber-200 shadow-lg rounded-xl p-3 max-w-xs"
                >
                    <div className="space-y-1.5">
                        <p className="text-xs font-semibold text-amber-800 border-b border-amber-100 pb-1.5 mb-2">
                            {filteredPannes.length} pannes détectées {hasNiveau3 && <span className="text-red-600">(Niveau 3)</span>}
                        </p>
                        {filteredPannes.map((panne, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-xs">
                                <Wrench className="w-3 h-3 text-amber-600 flex-shrink-0" />
                                <span className="text-amber-800">{panne}</span>
                            </div>
                        ))}
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

const CatalogPage = () => {
    const { token } = useAuth();
    const { addToCart, cart } = useCart();
    const navigate = useNavigate();
    
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterOptions, setFilterOptions] = useState({
        brands: [],
        models: [],
        capacities: [],
        colors: [],
        grades: [],
        conditions: [],
        defects: [],
        brand_models: {}  // New: mapping of brand -> models
    });
    
    const [filters, setFilters] = useState({
        brand: [],
        model: [],
        storage_capacity: [],
        color: [],
        grade: [],
        aesthetic_condition: [],
        cosmetic_condition: [],
        lock_status: [],
        defect: '',
        vat_regime: [],
        min_price: '',
        max_price: '',
        status: '',
        search: '',
        niveau_3: '',  // '', 'include', 'exclude'
        promo_id: '',   // Filter by promo ID
        badge_id: ''    // Filter by badge ID
    });
    
    const [activePromoFilter, setActivePromoFilter] = useState(null); // Track active promo filter for UI
    
    // Badge filter state
    const [activeBadges, setActiveBadges] = useState([]); // Badges with products
    const [activeBadgeFilter, setActiveBadgeFilter] = useState(null); // Current badge filter
    
    // New arrivals state
    const [newArrivals, setNewArrivals] = useState({ functional: [], to_repair: [], total_count: 0 });
    const [showNewArrivals, setShowNewArrivals] = useState(true);
    const [newArrivalsTab, setNewArrivalsTab] = useState('functional'); // 'functional' or 'to_repair'
    const [expandedNewArrival, setExpandedNewArrival] = useState(null); // Track expanded product in Nouveautés
    
    const [searchInput, setSearchInput] = useState('');
    const [sortBy, setSortBy] = useState('price');
    const [sortOrder, setSortOrder] = useState('desc'); // Par défaut: prix décroissant (du plus élevé au moins élevé)
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [addingToCart, setAddingToCart] = useState({});
    
    // Filter collapse state - filters visible by default, toggled manually
    const [showScrollTop, setShowScrollTop] = useState(false);
    const scrollContainerRef = useRef(null);
    const searchTimeoutRef = useRef(null);

    // Debounced search - auto-search after 300ms of typing
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        searchTimeoutRef.current = setTimeout(() => {
            if (searchInput !== filters.search) {
                setFilters(prev => ({ ...prev, search: searchInput }));
                setPage(1);
            }
        }, 300);
        
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchInput]);

    const cartItemIds = cart.items.map(item => item.id);

    // Handle scroll to show/hide scroll-to-top button
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            setShowScrollTop(container.scrollTop > 200);
        };

        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
    }, []);

    // Scroll to top function
    const scrollToTop = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            
            Object.entries(filters).forEach(([key, value]) => {
                if (Array.isArray(value) && value.length > 0) {
                    params.append(key, value.join(','));
                } else if (value && !Array.isArray(value)) {
                    params.append(key, value);
                }
            });
            
            params.append('sort_by', sortBy);
            params.append('sort_order', sortOrder);
            params.append('page', page.toString());
            params.append('limit', pageSize.toString());

            const response = await axios.get(`${API_URL}/products?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setProducts(response.data.products);
            setTotalPages(response.data.pages);
            setTotal(response.data.total);
        } catch (error) {
            console.error('Fetch products error:', error);
            toast.error('Erreur lors du chargement des produits');
        } finally {
            setLoading(false);
        }
    }, [token, filters, sortBy, sortOrder, page, pageSize]);

    const fetchFilterOptions = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/products/filters`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setFilterOptions(response.data);
        } catch (error) {
            console.error('Fetch filter options error:', error);
        }
    }, [token]);

    // Fetch active badges for filter display
    const fetchActiveBadges = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/badges/active`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setActiveBadges(response.data.badges || []);
        } catch (error) {
            console.error('Fetch active badges error:', error);
        }
    }, [token]);

    // Fetch new arrivals (products added in last 72h)
    const fetchNewArrivals = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/products/new-arrivals`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNewArrivals({
                functional: response.data.functional || [],
                to_repair: response.data.to_repair || [],
                total_count: response.data.total_count || 0
            });
        } catch (error) {
            console.error('Fetch new arrivals error:', error);
        }
    }, [token]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    useEffect(() => {
        fetchFilterOptions();
        fetchActiveBadges();
        fetchNewArrivals();
    }, [fetchFilterOptions, fetchActiveBadges, fetchNewArrivals]);

    const handleAddToCart = async (productId) => {
        setAddingToCart(prev => ({ ...prev, [productId]: true }));
        try {
            await addToCart(productId);
            toast.success('Produit ajouté au panier');
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de l\'ajout au panier';
            toast.error(message);
        } finally {
            setAddingToCart(prev => ({ ...prev, [productId]: false }));
        }
    };

    const handleAddSelectedToCart = async () => {
        for (const productId of selectedProducts) {
            if (!cartItemIds.includes(productId)) {
                await handleAddToCart(productId);
            }
        }
        setSelectedProducts([]);
    };

    const toggleProductSelection = (productId) => {
        setSelectedProducts(prev => 
            prev.includes(productId) 
                ? prev.filter(id => id !== productId)
                : [...prev, productId]
        );
    };

    const selectAllVisible = () => {
        const availableIds = products
            .filter(p => !cartItemIds.includes(p.id))
            .map(p => p.id);
        setSelectedProducts(availableIds);
    };

    const clearSelection = () => {
        setSelectedProducts([]);
    };

    const clearFilters = () => {
        setFilters({
            brand: [],
            model: [],
            storage_capacity: [],
            color: [],
            grade: [],
            aesthetic_condition: [],
            cosmetic_condition: [],
            lock_status: [],
            defect: '',
            vat_regime: [],
            min_price: '',
            max_price: '',
            status: '',
            niveau_3: '',
            promo_id: '',
            badge_id: ''
        });
        setActivePromoFilter(null);
        setActiveBadgeFilter(null);
        setPage(1);
    };

    // Handle promo filter from banner
    const handleFilterPromo = (promoId) => {
        if (filters.promo_id === promoId) {
            // Toggle off if already filtering by this promo
            setFilters(prev => ({ ...prev, promo_id: '' }));
            setActivePromoFilter(null);
        } else {
            setFilters(prev => ({ ...prev, promo_id: promoId }));
            setActivePromoFilter(promoId);
        }
        setPage(1);
    };

    // Handle badge filter
    const handleFilterBadge = (badgeId) => {
        if (filters.badge_id === badgeId) {
            // Toggle off if already filtering by this badge
            setFilters(prev => ({ ...prev, badge_id: '' }));
            setActiveBadgeFilter(null);
        } else {
            setFilters(prev => ({ ...prev, badge_id: badgeId }));
            setActiveBadgeFilter(badgeId);
        }
        setPage(1);
    };

    // Get filtered models based on selected brands
    const getFilteredModels = () => {
        if (filters.brand.length === 0) {
            // No brand selected - show all models
            return filterOptions.models;
        }
        // Get models only for selected brands
        const modelsForBrands = new Set();
        filters.brand.forEach(brand => {
            const brandModels = filterOptions.brand_models[brand] || [];
            brandModels.forEach(m => modelsForBrands.add(m));
        });
        return Array.from(modelsForBrands).sort();
    };

    // Clear model selection when brand changes and selected models are no longer available
    useEffect(() => {
        if (filters.brand.length > 0 && filters.model.length > 0) {
            const availableModels = getFilteredModels();
            const validModels = filters.model.filter(m => availableModels.includes(m));
            if (validModels.length !== filters.model.length) {
                setFilters(prev => ({ ...prev, model: validModels }));
            }
        }
    }, [filters.brand]);

    const getGradeBadge = (grade) => {
        const styles = {
            'A': 'bg-emerald-100 text-emerald-800 border-emerald-300',
            'A+': 'bg-emerald-100 text-emerald-800 border-emerald-300',
            'A-': 'bg-emerald-50 text-emerald-700 border-emerald-200',
            'B+': 'bg-blue-100 text-blue-800 border-blue-300',
            'B': 'bg-blue-50 text-blue-700 border-blue-200',
            'B-': 'bg-blue-50 text-blue-600 border-blue-200',
            'C': 'bg-amber-100 text-amber-800 border-amber-300',
            'D': 'bg-rose-100 text-rose-800 border-rose-300'
        };
        return styles[grade] || 'bg-slate-100 text-slate-600 border-slate-200';
    };

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            // Pour le prix, commencer par décroissant (du plus cher au moins cher)
            setSortOrder(field === 'price' ? 'desc' : 'asc');
        }
    };

    const SortIcon = ({ field }) => {
        if (sortBy !== field) return null;
        return sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />;
    };

    const getBatteryDisplay = (battery) => {
        if (!battery) return { text: '-', color: 'text-slate-400', icon: null };
        const batteryLower = battery.toLowerCase();
        
        if (batteryLower === 'neuve' || batteryLower === 'new' || batteryLower === '100%') {
            return { text: 'Neuve', color: 'text-emerald-600', icon: <BatteryFull className="w-4 h-4 text-emerald-600" /> };
        }
        if (batteryLower === 'sup_85' || batteryLower.includes('>85')) {
            return { text: '>85%', color: 'text-emerald-600', icon: <BatteryFull className="w-4 h-4 text-emerald-600" /> };
        }
        if (batteryLower === '80_85' || batteryLower === '80-85' || batteryLower.includes('80_85')) {
            return { text: '80-85%', color: 'text-emerald-600', icon: <BatteryFull className="w-4 h-4 text-emerald-600" /> };
        }
        if (batteryLower === 'ok' || batteryLower.includes('90')) {
            return { text: battery.toUpperCase(), color: 'text-emerald-600', icon: <BatteryFull className="w-4 h-4 text-emerald-600" /> };
        }
        if (batteryLower === 'inf_80' || batteryLower.includes('inf') || batteryLower.includes('<80')) {
            return { text: '<80%', color: 'text-amber-600', icon: <BatteryMedium className="w-4 h-4 text-amber-600" /> };
        }
        if (batteryLower === 'inconnu' || batteryLower === 'unknown') {
            return { text: '?', color: 'text-slate-500', icon: <BatteryLow className="w-4 h-4 text-slate-400" /> };
        }
        return { text: battery, color: 'text-slate-600', icon: null };
    };


    const activeFilterCount = Object.values(filters).filter(v => 
        Array.isArray(v) ? v.length > 0 : v
    ).length;

    return (
        <Layout>
            <div className="h-[calc(100vh-64px)] bg-gray-50 flex flex-col">
                
                {/* Fixed Header - Always visible */}
                <div className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
                    <div className="max-w-[1600px] mx-auto px-6 py-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 
                                    className="text-2xl font-bold text-gray-900 tracking-tight"
                                    style={{ fontFamily: 'var(--font-heading)' }}
                                >
                                    Catalogue
                                </h1>
                                <p className="text-sm text-gray-500 flex items-center gap-2">
                                    <Package className="w-4 h-4 text-blue-500" />
                                    {total} produits disponibles
                                </p>
                            </div>

                            <div className="flex items-center gap-3">
                                <Button 
                                    onClick={() => navigate('/cart')}
                                    className="rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700"
                                    data-testid="go-to-cart"
                                >
                                    <ShoppingCart className="w-4 h-4 mr-2" />
                                    Panier ({cart.items.length})
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scrollable Content Area - Contains filters + table header + products */}
                <div 
                    ref={scrollContainerRef}
                    className="flex-1 overflow-y-auto min-h-0"
                >
                    {/* Promo Featured Banner */}
                    <div className="max-w-[1600px] mx-auto px-6 pt-6">
                        <PromoFeaturedBanner onFilterPromo={handleFilterPromo} />
                        
                        {/* NEW ARRIVALS SECTION */}
                        {showNewArrivals && newArrivals.total_count > 0 && (
                            <div className="mb-6 bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 border border-emerald-200 rounded-2xl overflow-hidden">
                                {/* Header */}
                                <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-emerald-100/50 to-teal-100/50 border-b border-emerald-200">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-emerald-500 rounded-xl">
                                            <Sparkles className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-emerald-800 text-lg">Nouveautés</h3>
                                            <p className="text-sm text-emerald-600">
                                                {newArrivals.total_count} produit(s) ajouté(s) ces dernières 72h
                                            </p>
                                        </div>
                                    </div>
                                    
                                    {/* Tabs */}
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setNewArrivalsTab('functional')}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                                                newArrivalsTab === 'functional'
                                                    ? 'bg-emerald-500 text-white shadow-md'
                                                    : 'bg-white/60 text-emerald-700 hover:bg-white'
                                            }`}
                                        >
                                            <CheckCircle className="w-4 h-4" />
                                            Fonctionnels ({newArrivals.functional.length})
                                        </button>
                                        <button
                                            onClick={() => setNewArrivalsTab('to_repair')}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                                                newArrivalsTab === 'to_repair'
                                                    ? 'bg-amber-500 text-white shadow-md'
                                                    : 'bg-white/60 text-amber-700 hover:bg-white'
                                            }`}
                                        >
                                            <Wrench className="w-4 h-4" />
                                            À réparer ({newArrivals.to_repair.length})
                                        </button>
                                        <button
                                            onClick={() => setShowNewArrivals(false)}
                                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-lg ml-2"
                                            title="Masquer les nouveautés"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Products Carousel */}
                                <div className="px-5 py-4">
                                    <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-emerald-300 scrollbar-track-transparent">
                                        {(newArrivalsTab === 'functional' ? newArrivals.functional : newArrivals.to_repair).slice(0, 20).map((product) => {
                                            const isExpanded = expandedNewArrival === product.id;
                                            const isInCart = cartItemIds.includes(product.id);
                                            const isAdding = addingToCart[product.id];
                                            
                                            // Get battery info
                                            const getBatteryInfo = (health) => {
                                                if (!health || health === '-') return { icon: '🔋', text: '-', color: 'text-gray-400' };
                                                const num = parseInt(health);
                                                if (isNaN(num)) return { icon: '🔋', text: health, color: 'text-gray-500' };
                                                if (num >= 85) return { icon: '🔋', text: `${num}%`, color: 'text-emerald-600' };
                                                if (num >= 70) return { icon: '🔋', text: `${num}%`, color: 'text-amber-600' };
                                                return { icon: '🪫', text: `${num}%`, color: 'text-red-600' };
                                            };
                                            const batteryInfo = getBatteryInfo(product.battery_health);
                                            
                                            return (
                                            <div 
                                                key={product.id}
                                                className={`flex-shrink-0 bg-white rounded-xl border shadow-sm hover:shadow-lg transition-all ${
                                                    isExpanded 
                                                        ? 'w-[450px] border-emerald-400 shadow-lg' 
                                                        : 'w-[220px] border-gray-200 hover:border-emerald-300 cursor-pointer'
                                                }`}
                                                onClick={() => !isExpanded && setExpandedNewArrival(product.id)}
                                            >
                                                {/* Time Badge */}
                                                <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-gray-100 rounded-t-xl">
                                                    <div className="flex items-center gap-1.5 text-emerald-700">
                                                        <Clock className="w-3.5 h-3.5" />
                                                        <span className="text-xs font-semibold">
                                                            En ligne depuis {product.time_since?.display || 'Récent'}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {product.promo && (
                                                            <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded">
                                                                PROMO
                                                            </span>
                                                        )}
                                                        {isExpanded && (
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); setExpandedNewArrival(null); }}
                                                                className="p-1 hover:bg-white/50 rounded"
                                                            >
                                                                <X className="w-4 h-4 text-gray-500" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                {/* Product Info - Expanded or Compact */}
                                                <div className="p-3">
                                                    {!isExpanded ? (
                                                        /* Compact View */
                                                        <>
                                                            <div className="flex items-start gap-2 mb-2">
                                                                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                                    <Smartphone className="w-5 h-5 text-gray-400" />
                                                                </div>
                                                                <div className="min-w-0 flex-1">
                                                                    <p className="font-semibold text-gray-900 text-sm truncate">
                                                                        {product.brand} {product.model}
                                                                    </p>
                                                                    <p className="text-xs text-gray-500 truncate">
                                                                        {product.storage_capacity}Go • {product.color}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                                                    product.grade === 'A' ? 'bg-green-100 text-green-700' :
                                                                    product.grade === 'B' ? 'bg-blue-100 text-blue-700' :
                                                                    product.grade === 'C' ? 'bg-yellow-100 text-yellow-700' :
                                                                    'bg-gray-100 text-gray-600'
                                                                }`}>
                                                                    {product.grade}
                                                                </span>
                                                                <span className={`text-xs font-medium ${
                                                                    product.erp_etat?.toLowerCase() === 'fonctionnel' ? 'text-green-600' : 'text-amber-600'
                                                                }`}>
                                                                    {product.erp_etat?.toLowerCase() === 'fonctionnel' ? '✓ Fonctionnel' : '🔧 À réparer'}
                                                                </span>
                                                            </div>
                                                            
                                                            <div className="text-center text-xs text-emerald-600 font-medium py-1">
                                                                Cliquez pour voir les détails →
                                                            </div>
                                                            
                                                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                                                                <div>
                                                                    <span className="text-lg font-bold text-blue-600">
                                                                        {Math.ceil(product.price)}€
                                                                    </span>
                                                                    <span className="text-xs text-gray-400 ml-1">HT</span>
                                                                </div>
                                                                <Button
                                                                    size="sm"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleAddToCart(product.id);
                                                                    }}
                                                                    disabled={isInCart || isAdding}
                                                                    className={`rounded-lg ${
                                                                        isInCart
                                                                            ? 'bg-green-500 hover:bg-green-500'
                                                                            : 'bg-blue-600 hover:bg-blue-700'
                                                                    }`}
                                                                >
                                                                    {isInCart ? (
                                                                        <Check className="w-4 h-4" />
                                                                    ) : isAdding ? (
                                                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                                                    ) : (
                                                                        <Plus className="w-4 h-4" />
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        /* Expanded View - Full Details */
                                                        <div className="space-y-3">
                                                            {/* Header */}
                                                            <div className="flex items-start gap-3">
                                                                <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                                                    <Smartphone className="w-7 h-7 text-gray-400" />
                                                                </div>
                                                                <div className="flex-1">
                                                                    <p className="font-bold text-gray-900 text-lg">
                                                                        {product.brand} {product.model}
                                                                    </p>
                                                                    <p className="text-xs text-gray-400 font-mono">{product.imei}</p>
                                                                    <div className="flex items-center gap-2 mt-1">
                                                                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                                                                            product.vat_regime === 'margin' 
                                                                                ? 'bg-blue-100 text-blue-700' 
                                                                                : 'bg-gray-100 text-gray-700'
                                                                        }`}>
                                                                            {product.vat_regime === 'margin' ? 'TVA Marge' : 'TVA Std'}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="text-right">
                                                                    <span className="text-2xl font-bold text-blue-600">
                                                                        {Math.ceil(product.price)}€
                                                                    </span>
                                                                    <span className="text-sm text-gray-400 ml-1">HT</span>
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Details Grid */}
                                                            <div className="grid grid-cols-4 gap-2 py-2 border-y border-gray-100">
                                                                {/* Storage */}
                                                                <div className="text-center p-2 bg-gray-50 rounded-lg">
                                                                    <div className="flex items-center justify-center gap-1 text-gray-600">
                                                                        <HardDrive className="w-4 h-4" />
                                                                    </div>
                                                                    <p className="font-bold text-gray-900">{product.storage_capacity}Go</p>
                                                                    {product.version && (
                                                                        <p className="text-[10px] text-blue-600">{product.version}</p>
                                                                    )}
                                                                </div>
                                                                
                                                                {/* Color */}
                                                                <div className="text-center p-2 bg-gray-50 rounded-lg">
                                                                    <p className="text-[10px] text-gray-500 uppercase">Couleur</p>
                                                                    <p className="font-bold text-gray-900 text-sm truncate">{product.color || '-'}</p>
                                                                </div>
                                                                
                                                                {/* Grade */}
                                                                <div className="text-center p-2 bg-gray-50 rounded-lg">
                                                                    <p className="text-[10px] text-gray-500 uppercase">Grade</p>
                                                                    <span className={`inline-block px-2 py-0.5 rounded text-sm font-bold ${
                                                                        product.grade === 'A' ? 'bg-green-100 text-green-700' :
                                                                        product.grade === 'B' ? 'bg-blue-100 text-blue-700' :
                                                                        product.grade === 'C' ? 'bg-yellow-100 text-yellow-700' :
                                                                        'bg-gray-100 text-gray-600'
                                                                    }`}>
                                                                        {product.grade || '-'}
                                                                    </span>
                                                                </div>
                                                                
                                                                {/* Battery */}
                                                                <div className="text-center p-2 bg-gray-50 rounded-lg">
                                                                    <p className="text-[10px] text-gray-500 uppercase">Batterie</p>
                                                                    <p className={`font-bold text-sm ${batteryInfo.color}`}>
                                                                        {batteryInfo.icon} {batteryInfo.text}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Status Row */}
                                                            <div className="grid grid-cols-3 gap-2">
                                                                {/* État */}
                                                                <div className="p-2 bg-gray-50 rounded-lg">
                                                                    <p className="text-[10px] text-gray-500 uppercase mb-1">État</p>
                                                                    <div className={`flex items-center gap-1 text-xs font-medium ${
                                                                        product.aesthetic_condition === 'Fonctionnel' || product.erp_etat?.toLowerCase() === 'fonctionnel' 
                                                                            ? 'text-emerald-600' : 'text-amber-600'
                                                                    }`}>
                                                                        {product.aesthetic_condition === 'Fonctionnel' || product.erp_etat?.toLowerCase() === 'fonctionnel' ? (
                                                                            <CheckCircle className="w-4 h-4" />
                                                                        ) : (
                                                                            <Wrench className="w-4 h-4" />
                                                                        )}
                                                                        <span>{product.aesthetic_condition || product.erp_etat || '-'}</span>
                                                                    </div>
                                                                </div>
                                                                
                                                                {/* Lock Status */}
                                                                <div className="p-2 bg-gray-50 rounded-lg">
                                                                    <p className="text-[10px] text-gray-500 uppercase mb-1">Verrouillage</p>
                                                                    <div className="flex items-center gap-1 text-xs font-medium">
                                                                        {!product.lock_status || product.lock_status === '-' || product.lock_status === 'Débloqué' ? (
                                                                            <>
                                                                                <Unlock className="w-4 h-4 text-emerald-600" />
                                                                                <span className="text-emerald-600">{product.lock_status || 'Débloqué'}</span>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <Lock className="w-4 h-4 text-red-600" />
                                                                                <span className="text-red-600">{product.lock_status}</span>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                
                                                                {/* Cosmetic */}
                                                                <div className="p-2 bg-gray-50 rounded-lg">
                                                                    <p className="text-[10px] text-gray-500 uppercase mb-1">Cosmétique</p>
                                                                    <p className={`text-xs font-medium ${
                                                                        !product.cosmetic_condition || product.cosmetic_condition === 'OK' 
                                                                            ? 'text-emerald-600' : 'text-amber-600'
                                                                    }`}>
                                                                        {product.cosmetic_condition || 'OK'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Pannes & Pièces inconnues Row */}
                                                            <div className="grid grid-cols-2 gap-2">
                                                                {/* Pannes */}
                                                                {(() => {
                                                                    const filteredPannes = filterPannes(product.pannes);
                                                                    const hasPannes = filteredPannes.length > 0;
                                                                    return (
                                                                        <div className={`p-2 rounded-lg ${hasPannes ? 'bg-red-50' : 'bg-emerald-50'}`}>
                                                                            <p className={`text-[10px] uppercase mb-1 ${hasPannes ? 'text-red-500' : 'text-emerald-600'}`}>Pannes</p>
                                                                            {hasPannes ? (
                                                                                <div className="flex flex-wrap gap-1">
                                                                                    {filteredPannes.map((panne, idx) => (
                                                                                        <span key={idx} className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded">
                                                                                            {panne}
                                                                                        </span>
                                                                                    ))}
                                                                                </div>
                                                                            ) : (
                                                                                <div className="flex items-center gap-1 text-emerald-600 font-medium text-xs">
                                                                                    <CheckCircle className="w-4 h-4" />
                                                                                    <span>Aucune</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })()}
                                                                
                                                                {/* Pièces inconnues */}
                                                                <div className={`p-2 rounded-lg ${
                                                                    product.pieces_inconnues && product.pieces_inconnues.length > 0 
                                                                        ? 'bg-amber-50' : 'bg-emerald-50'
                                                                }`}>
                                                                    <p className={`text-[10px] uppercase mb-1 ${
                                                                        product.pieces_inconnues && product.pieces_inconnues.length > 0 
                                                                            ? 'text-amber-600' : 'text-emerald-600'
                                                                    }`}>Pièces Inc.</p>
                                                                    {product.pieces_inconnues && product.pieces_inconnues.length > 0 ? (
                                                                        <div className="flex flex-wrap gap-1">
                                                                            {product.pieces_inconnues.map((piece, idx) => (
                                                                                <span key={idx} className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                                                                                    {piece}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="flex items-center gap-1 text-emerald-600 font-medium text-xs">
                                                                            <CheckCircle className="w-4 h-4" />
                                                                            <span>Aucune</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Niveau 3 Badge */}
                                                            {product.has_niveau_3 && (
                                                                <div className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg">
                                                                    <span className="text-purple-600 font-bold">⚡ Niveau 3</span>
                                                                    <span className="text-xs text-purple-500">Diagnostic approfondi disponible</span>
                                                                </div>
                                                            )}
                                                            
                                                            {/* Add to Cart Button */}
                                                            <Button
                                                                className={`w-full h-12 text-lg font-bold rounded-xl ${
                                                                    isInCart
                                                                        ? 'bg-green-500 hover:bg-green-500'
                                                                        : 'bg-blue-600 hover:bg-blue-700'
                                                                }`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleAddToCart(product.id);
                                                                }}
                                                                disabled={isInCart || isAdding}
                                                            >
                                                                {isInCart ? (
                                                                    <>
                                                                        <Check className="w-5 h-5 mr-2" />
                                                                        Dans le panier
                                                                    </>
                                                                ) : isAdding ? (
                                                                    <>
                                                                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                                                                        Ajout...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <ShoppingCart className="w-5 h-5 mr-2" />
                                                                        Ajouter au panier - {Math.ceil(product.price)}€
                                                                    </>
                                                                )}
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )})}
                                        
                                        {/* Show more indicator */}
                                        {(newArrivalsTab === 'functional' ? newArrivals.functional : newArrivals.to_repair).length > 20 && (
                                            <div className="flex-shrink-0 w-[120px] flex items-center justify-center">
                                                <div className="text-center text-emerald-600">
                                                    <p className="text-2xl font-bold">+{(newArrivalsTab === 'functional' ? newArrivals.functional : newArrivals.to_repair).length - 20}</p>
                                                    <p className="text-xs">autres</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Show New Arrivals Button (when hidden) */}
                        {!showNewArrivals && newArrivals.total_count > 0 && (
                            <button
                                onClick={() => setShowNewArrivals(true)}
                                className="mb-4 flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-xl hover:bg-emerald-200 transition-colors"
                            >
                                <Sparkles className="w-4 h-4" />
                                <span className="font-medium">Afficher les nouveautés ({newArrivals.total_count})</span>
                            </button>
                        )}
                        
                        {/* Active Promo Filter Indicator */}
                        {activePromoFilter && (
                            <div className="mb-4 flex items-center gap-2 p-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl">
                                <Flame className="w-5 h-5 text-orange-500" />
                                <span className="text-amber-800 font-medium">
                                    Filtré par promotion active
                                </span>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleFilterPromo(activePromoFilter)}
                                    className="ml-auto text-amber-700 hover:text-amber-900 hover:bg-amber-100"
                                >
                                    <X className="w-4 h-4 mr-1" />
                                    Retirer le filtre
                                </Button>
                            </div>
                        )}
                        
                        {/* Active Badge Filter Indicator */}
                        {activeBadgeFilter && (
                            <div className="mb-4 flex items-center gap-2 p-3 rounded-xl border"
                                style={{ 
                                    backgroundColor: (activeBadges.find(b => b.id === activeBadgeFilter)?.bg_color || '#f97316') + '10',
                                    borderColor: (activeBadges.find(b => b.id === activeBadgeFilter)?.bg_color || '#f97316') + '40'
                                }}
                            >
                                <Tag className="w-5 h-5" style={{ color: activeBadges.find(b => b.id === activeBadgeFilter)?.bg_color }} />
                                <span className="font-medium" style={{ color: activeBadges.find(b => b.id === activeBadgeFilter)?.bg_color }}>
                                    Filtré par badge: {activeBadges.find(b => b.id === activeBadgeFilter)?.name}
                                </span>
                                <span className="text-sm text-slate-500">
                                    ({activeBadges.find(b => b.id === activeBadgeFilter)?.product_count} produits)
                                </span>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleFilterBadge(activeBadgeFilter)}
                                    className="ml-auto hover:bg-white/50"
                                    style={{ color: activeBadges.find(b => b.id === activeBadgeFilter)?.bg_color }}
                                >
                                    <X className="w-4 h-4 mr-1" />
                                    Retirer le filtre
                                </Button>
                            </div>
                        )}
                    </div>
                    
                    {/* Filters Section - Always visible */}
                    <div className="bg-white border-b border-gray-100">
                        <div className="max-w-[1600px] mx-auto px-6 py-4">
                            {/* Status Filter - Visual Buttons */}
                            <div className="flex gap-3 mb-4 pb-4 border-b border-gray-100">
                            <button
                                onClick={() => setFilters({ ...filters, status: '' })}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                                    filters.status === '' 
                                        ? 'bg-blue-600 text-white shadow-md' 
                                        : 'bg-white text-gray-600 hover:bg-blue-50 border border-gray-200 hover:border-blue-200'
                                }`}
                                data-testid="filter-status-all"
                            >
                                <Smartphone className="w-4 h-4" />
                                <span>Tous</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${filters.status === '' ? 'bg-white/20' : 'bg-gray-100'}`}>
                                    {total}
                                </span>
                            </button>
                            <button
                                onClick={() => setFilters({ ...filters, status: 'fonctionnel' })}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                                    filters.status === 'fonctionnel' 
                                        ? 'bg-emerald-500 text-white shadow-md' 
                                        : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                                }`}
                                data-testid="filter-status-fonctionnel"
                            >
                                <CheckCircle className="w-4 h-4" />
                                <span>Fonctionnel</span>
                            </button>
                            <button
                                onClick={() => setFilters({ ...filters, status: 'a_reparer' })}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                                    filters.status === 'a_reparer' 
                                        ? 'bg-amber-500 text-white shadow-md' 
                                        : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                                }`}
                                data-testid="filter-status-reparer"
                            >
                                <Wrench className="w-4 h-4" />
                                <span>À réparer</span>
                            </button>
                            
                            {/* Niveau 3 Filter - Separator */}
                            <div className="h-8 w-px bg-gray-200 mx-2" />
                            
                            {/* Niveau 3 Filter Buttons */}
                            <button
                                onClick={() => setFilters({ ...filters, niveau_3: filters.niveau_3 === 'exclude' ? '' : 'exclude' })}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                                    filters.niveau_3 === 'exclude' 
                                        ? 'bg-emerald-500 text-white shadow-md' 
                                        : 'bg-white text-gray-600 hover:bg-emerald-50 border border-gray-200 hover:border-emerald-200'
                                }`}
                                data-testid="filter-niveau3-exclude"
                            >
                                <X className="w-4 h-4" />
                                <span>Sans Niv.3</span>
                            </button>
                            <button
                                onClick={() => setFilters({ ...filters, niveau_3: filters.niveau_3 === 'include' ? '' : 'include' })}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                                    filters.niveau_3 === 'include' 
                                        ? 'bg-red-500 text-white shadow-md' 
                                        : 'bg-white text-gray-600 hover:bg-red-50 border border-gray-200 hover:border-red-200'
                                }`}
                                data-testid="filter-niveau3-include"
                            >
                                <AlertTriangle className="w-4 h-4" />
                                <span>Avec Niv.3</span>
                            </button>
                            
                            {/* Badge Filters */}
                            {activeBadges.length > 0 && (
                                <>
                                    <div className="h-8 w-px bg-gray-200 mx-2" />
                                    {activeBadges.map((badge) => (
                                        <button
                                            key={badge.id}
                                            onClick={() => handleFilterBadge(badge.id)}
                                            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                                                activeBadgeFilter === badge.id
                                                    ? 'shadow-md ring-2 ring-offset-1'
                                                    : 'bg-white border border-gray-200 hover:border-gray-300'
                                            }`}
                                            style={activeBadgeFilter === badge.id ? {
                                                backgroundColor: badge.bg_color,
                                                color: badge.text_color,
                                                ringColor: badge.bg_color
                                            } : {
                                                color: badge.bg_color,
                                                borderColor: badge.bg_color + '40'
                                            }}
                                            data-testid={`filter-badge-${badge.id}`}
                                        >
                                            <Tag className="w-4 h-4" />
                                            <span>{badge.name}</span>
                                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                                                activeBadgeFilter === badge.id 
                                                    ? 'bg-white/20' 
                                                    : 'bg-gray-100'
                                            }`}>
                                                {badge.product_count}
                                            </span>
                                        </button>
                                    ))}
                                </>
                            )}
                        </div>

                        {/* Filter Bar */}
                        <div className="flex flex-wrap gap-3 items-end">
                            {/* Search Field - Live search */}
                            <div className="w-64">
                                <label className="text-xs font-semibold text-blue-600 mb-1.5 block">Recherche</label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <Input
                                        type="text"
                                        placeholder="Modèle, IMEI..."
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        className="pl-9 pr-8 h-10 rounded-lg border-slate-200 focus:border-blue-400 focus:ring-blue-400"
                                        data-testid="search-input"
                                    />
                                    {searchInput && (
                                        <button
                                            onClick={() => {
                                                setSearchInput('');
                                            }}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
                                        >
                                            <X className="w-4 h-4 text-slate-400" />
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="w-40">
                                <label className="text-xs font-semibold text-blue-600 mb-1.5 block">Marque</label>
                                <MultiSelect
                                    data-testid="filter-brand"
                                    options={filterOptions.brands.map(b => ({ value: b, label: b }))}
                                    selected={filters.brand}
                                    onChange={(v) => setFilters({ ...filters, brand: v })}
                                    placeholder="Toutes"
                                    maxDisplay={1}
                                />
                            </div>

                            <div className="w-44">
                                <label className="text-xs font-semibold text-blue-600 mb-1.5 block">Modèle</label>
                                <MultiSelect
                                    data-testid="filter-model"
                                    options={getFilteredModels().map(m => ({ value: m, label: m }))}
                                    selected={filters.model}
                                    onChange={(v) => setFilters({ ...filters, model: v })}
                                    placeholder="Tous"
                                    maxDisplay={1}
                                />
                            </div>

                            <div className="w-32">
                                <label className="text-xs font-semibold text-blue-600 mb-1.5 block">Capacité</label>
                                <MultiSelect
                                    data-testid="filter-storage"
                                    options={filterOptions.capacities.map(c => ({ value: c.toString(), label: `${c} GB` }))}
                                    selected={filters.storage_capacity}
                                    onChange={(v) => setFilters({ ...filters, storage_capacity: v })}
                                    placeholder="Toutes"
                                    maxDisplay={1}
                                />
                            </div>

                            <div className="w-28">
                                <label className="text-xs font-semibold text-blue-600 mb-1.5 block">Grade</label>
                                <MultiSelect
                                    data-testid="filter-grade"
                                    options={filterOptions.grades.map(g => ({ value: g, label: g }))}
                                    selected={filters.grade}
                                    onChange={(v) => setFilters({ ...filters, grade: v })}
                                    placeholder="Tous"
                                    maxDisplay={2}
                                />
                            </div>

                            <div className="w-36">
                                <label className="text-xs font-semibold text-blue-600 mb-1.5 block">Cosmétique</label>
                                <MultiSelect
                                    data-testid="filter-cosmetic"
                                    options={[
                                        { value: 'ok', label: 'OK' },
                                        { value: 'ecran', label: 'Écran cassé' },
                                        { value: 'vitre_arriere', label: 'Arrière cassé' }
                                    ]}
                                    selected={filters.cosmetic_condition}
                                    onChange={(v) => setFilters({ ...filters, cosmetic_condition: v })}
                                    placeholder="Tous"
                                    maxDisplay={1}
                                />
                            </div>

                            <div className="w-32">
                                <label className="text-xs font-semibold text-blue-600 mb-1.5 block">Lock</label>
                                <MultiSelect
                                    data-testid="filter-lock"
                                    options={[
                                        { value: 'debloque', label: 'Débloqué' },
                                        { value: 'icloud', label: 'iCloud' },
                                        { value: 'mdm', label: 'MDM' },
                                        { value: 'knox', label: 'Knox' },
                                        { value: 'google', label: 'Google/FRP' }
                                    ]}
                                    selected={filters.lock_status}
                                    onChange={(v) => setFilters({ ...filters, lock_status: v })}
                                    placeholder="Tous"
                                    maxDisplay={1}
                                />
                            </div>

                            <div className="w-32">
                                <label className="text-xs font-semibold text-blue-600 mb-1.5 block">TVA</label>
                                <MultiSelect
                                    data-testid="filter-vat"
                                    options={[
                                        { value: 'margin', label: 'Marge' },
                                        { value: 'standard', label: 'Standard' }
                                    ]}
                                    selected={filters.vat_regime}
                                    onChange={(v) => setFilters({ ...filters, vat_regime: v })}
                                    placeholder="Tous"
                                    maxDisplay={2}
                                />
                            </div>

                            <div className="w-24">
                                <label className="text-xs font-semibold text-blue-600 mb-1.5 block">Prix min</label>
                                <Input
                                    type="number"
                                    placeholder="Min €"
                                    value={filters.min_price}
                                    onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
                                    className="h-10 rounded-lg border-gray-200 focus:border-blue-400 focus:ring-blue-200"
                                    data-testid="filter-min-price"
                                />
                            </div>
                            <div className="w-24">
                                <label className="text-xs font-semibold text-blue-600 mb-1.5 block">Prix max</label>
                                <Input
                                    type="number"
                                    placeholder="Max €"
                                    value={filters.max_price}
                                    onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
                                    className="h-10 rounded-lg border-gray-200 focus:border-blue-400 focus:ring-blue-200"
                                    data-testid="filter-max-price"
                                />
                            </div>

                            {activeFilterCount > 0 && (
                                <Button 
                                    variant="ghost" 
                                    onClick={clearFilters} 
                                    className="h-10 rounded-lg text-rose-600 hover:text-rose-700 hover:bg-rose-50 font-medium"
                                    data-testid="clear-filters"
                                >
                                    Effacer ({activeFilterCount})
                                </Button>
                            )}

                            <div className="flex items-center gap-3 ml-auto">
                                <span className="text-sm text-gray-500 font-medium">
                                    {total} résultat{total > 1 ? 's' : ''}
                                </span>
                                <select
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(Number(e.target.value));
                                        setPage(1);
                                    }}
                                    className="h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-700 bg-white hover:border-blue-300 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 cursor-pointer"
                                    data-testid="page-size-select"
                                >
                                    <option value={20}>20 / page</option>
                                    <option value={50}>50 / page</option>
                                    <option value={100}>100 / page</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Selection Actions */}
                {selectedProducts.length > 0 && (
                    <div className="max-w-[1600px] mx-auto px-6 mt-4 relative z-10">
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
                            <span className="text-blue-800 font-semibold">
                                {selectedProducts.length} produit(s) sélectionné(s)
                            </span>
                            <div className="flex gap-3">
                                <Button 
                                    variant="outline" 
                                    onClick={clearSelection} 
                                    className="rounded-lg border-blue-200 text-blue-700 hover:bg-blue-100"
                                >
                                    Désélectionner
                                </Button>
                                <Button 
                                    onClick={handleAddSelectedToCart} 
                                    className="rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700"
                                    data-testid="add-selected-to-cart"
                                >
                                    <ShoppingCart className="w-4 h-4 mr-2" />
                                    Ajouter au panier
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Products */}
                <div className="max-w-[1600px] mx-auto px-6 py-6 relative z-10">
                    {loading ? (
                        <div className="flex items-center justify-center py-20">
                            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                        </div>
                    ) : products.length === 0 ? (
                        <div className="bg-white rounded-2xl border border-gray-200 p-16 text-center shadow-sm">
                            <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Package className="w-10 h-10 text-blue-400" />
                            </div>
                            <h3 
                                className="text-2xl font-bold text-gray-900 mb-3"
                                style={{ fontFamily: 'var(--font-heading)' }}
                            >
                                Aucun produit trouvé
                            </h3>
                            <p className="text-gray-500 mb-6">Essayez de modifier vos filtres pour voir plus de résultats</p>
                            <Button 
                                variant="outline" 
                                onClick={clearFilters}
                                className="rounded-lg border-blue-200 text-blue-700 hover:bg-blue-50"
                            >
                                Effacer les filtres
                            </Button>
                        </div>
                    ) : (
                        <div className="flex flex-col">
                            {/* Table Header - Sticky */}
                            <div className="bg-white px-4 py-3 border border-gray-200 border-b-0 rounded-t-xl sticky top-0 z-10">
                                <div className="flex items-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    <div className="w-8 flex-shrink-0">
                                        <Checkbox 
                                            checked={selectedProducts.length === products.filter(p => !cartItemIds.includes(p.id)).length && selectedProducts.length > 0}
                                            onCheckedChange={(checked) => checked ? selectAllVisible() : clearSelection()}
                                            className="border-gray-300"
                                        />
                                    </div>
                                    <button className="w-[300px] flex-shrink-0 flex items-center gap-1 hover:text-blue-600 text-left" onClick={() => toggleSort('model')}>
                                        Produit <SortIcon field="model" />
                                    </button>
                                    <div className="flex-1 flex items-center">
                                        <div className="text-center min-w-[60px] flex-1">Grade</div>
                                        <div className="text-center min-w-[90px] flex-1">État</div>
                                        <div className="text-center min-w-[80px] flex-1">Lock</div>
                                        <div className="text-center min-w-[90px] flex-1">Cosmétique</div>
                                        <div className="text-center min-w-[80px] flex-1">Pannes</div>
                                        <div className="text-center min-w-[80px] flex-1">Pièces inc.</div>
                                        <div className="text-center min-w-[70px] flex-1">Batterie</div>
                                        <button className="text-center min-w-[80px] flex-1 flex items-center justify-center gap-1 hover:text-blue-600" onClick={() => toggleSort('price')}>
                                            Prix HT <SortIcon field="price" />
                                        </button>
                                        <div className="text-center min-w-[50px] flex-1">Action</div>
                                    </div>
                                </div>
                            </div>

                            {/* Product List */}
                            <div className="border border-gray-200 border-t-0 rounded-b-xl bg-gray-50">
                                <div className="flex flex-col gap-2 p-2">
                            {products.map((product) => {
                                const isInCart = cartItemIds.includes(product.id);
                                const isSelected = selectedProducts.includes(product.id);
                                const isAdding = addingToCart[product.id];
                                
                                // Format price - rounded up, no cents
                                const priceRounded = Math.ceil(product.price);

                                // Lock display logic
                                const getLockDisplay = () => {
                                    if (!product.lock_status || product.lock_status === '-') {
                                        return { icon: <Unlock className="w-4 h-4 text-emerald-500" />, text: 'Débloqué', className: 'text-emerald-600' };
                                    }
                                    if (product.lock_status === 'Débloqué' || product.lock_status.toLowerCase() === 'unlocked') {
                                        return { icon: <Unlock className="w-4 h-4 text-emerald-500" />, text: 'Débloqué', className: 'text-emerald-600' };
                                    }
                                    if (product.lock_status === 'iCloud' || product.lock_status.toLowerCase().includes('icloud')) {
                                        return { icon: <Cloud className="w-4 h-4 text-rose-500" />, text: 'iCloud', className: 'text-rose-600 font-semibold' };
                                    }
                                    if (product.lock_status === 'MDM' || product.lock_status.toLowerCase().includes('mdm')) {
                                        return { icon: <HardDrive className="w-4 h-4 text-amber-500" />, text: 'MDM', className: 'text-amber-600 font-semibold' };
                                    }
                                    if (product.lock_status === 'Knox' || product.lock_status.toLowerCase().includes('knox')) {
                                        return { icon: <Lock className="w-4 h-4 text-purple-500" />, text: 'Knox', className: 'text-purple-600 font-semibold' };
                                    }
                                    if (product.lock_status.toLowerCase().includes('google') || product.lock_status.toLowerCase().includes('frp')) {
                                        return { icon: <Lock className="w-4 h-4 text-orange-500" />, text: 'Google/FRP', className: 'text-orange-600 font-semibold' };
                                    }
                                    return { icon: <Lock className="w-4 h-4 text-slate-400" />, text: product.lock_status, className: 'text-slate-600' };
                                };

                                const lockInfo = getLockDisplay();

                                // Get color dot based on product color
                                const getColorDot = (colorName) => {
                                    if (!colorName) return 'bg-slate-300';
                                    const color = colorName.toLowerCase();
                                    
                                    // Blacks
                                    if (color.includes('noir') || color.includes('black') || color.includes('graphite') || color.includes('midnight') || color.includes('minuit')) {
                                        return 'bg-gray-900';
                                    }
                                    // Whites
                                    if (color.includes('blanc') || color.includes('white') || color.includes('starlight') || color.includes('lumière') || color.includes('silver') || color.includes('argent')) {
                                        return 'bg-gray-100 border-gray-300';
                                    }
                                    // Golds
                                    if (color.includes('or') || color.includes('gold') || color.includes('doré')) {
                                        return 'bg-amber-300';
                                    }
                                    // Rose/Pink
                                    if (color.includes('rose') || color.includes('pink')) {
                                        return 'bg-pink-300';
                                    }
                                    // Blues
                                    if (color.includes('bleu') || color.includes('blue') || color.includes('sierra') || color.includes('pacifique') || color.includes('pacific')) {
                                        return 'bg-blue-500';
                                    }
                                    // Greens
                                    if (color.includes('vert') || color.includes('green') || color.includes('alpin') || color.includes('alpine') || color.includes('sauge') || color.includes('sage')) {
                                        return 'bg-green-500';
                                    }
                                    // Purples
                                    if (color.includes('violet') || color.includes('purple') || color.includes('mauve')) {
                                        return 'bg-purple-500';
                                    }
                                    // Reds
                                    if (color.includes('rouge') || color.includes('red') || color.includes('product red')) {
                                        return 'bg-red-500';
                                    }
                                    // Yellows
                                    if (color.includes('jaune') || color.includes('yellow')) {
                                        return 'bg-yellow-400';
                                    }
                                    // Oranges
                                    if (color.includes('orange') || color.includes('corail') || color.includes('coral')) {
                                        return 'bg-orange-400';
                                    }
                                    // Grays
                                    if (color.includes('gris') || color.includes('gray') || color.includes('grey') || color.includes('space') || color.includes('sidéral')) {
                                        return 'bg-gray-500';
                                    }
                                    // Titaniums
                                    if (color.includes('titane') || color.includes('titanium')) {
                                        if (color.includes('naturel') || color.includes('natural')) return 'bg-gray-300';
                                        if (color.includes('bleu') || color.includes('blue')) return 'bg-blue-400';
                                        if (color.includes('noir') || color.includes('black')) return 'bg-gray-800';
                                        if (color.includes('blanc') || color.includes('white')) return 'bg-gray-100 border-gray-300';
                                        return 'bg-gray-400';
                                    }
                                    // Creams/Beiges
                                    if (color.includes('crème') || color.includes('cream') || color.includes('beige')) {
                                        return 'bg-amber-100 border-amber-200';
                                    }
                                    
                                    return 'bg-slate-400';
                                };

                                // Cosmetic condition full text - stacked display
                                const getCosmeticDisplay = () => {
                                    if (!product.cosmetic_condition || product.cosmetic_condition === 'OK') {
                                        return { lines: ['OK'], className: 'text-emerald-600', icon: <CheckCircle className="w-3.5 h-3.5" /> };
                                    }
                                    const conditions = [];
                                    if (product.cosmetic_condition.toLowerCase().includes('écran') || product.cosmetic_condition.toLowerCase().includes('ecran')) {
                                        conditions.push('Écran cassé');
                                    }
                                    if (product.cosmetic_condition.toLowerCase().includes('arrière') || product.cosmetic_condition.toLowerCase().includes('arriere') || product.cosmetic_condition.toLowerCase().includes('back')) {
                                        conditions.push('Arrière cassé');
                                    }
                                    if (conditions.length === 0) {
                                        conditions.push(product.cosmetic_condition);
                                    }
                                    return { lines: conditions, className: 'text-amber-600', icon: <AlertTriangle className="w-3.5 h-3.5" /> };
                                };

                                const cosmeticInfo = getCosmeticDisplay();

                                return (
                                    <div 
                                        key={product.id}
                                        className={`bg-white rounded-xl border px-4 py-3 transition-all duration-200 hover:shadow-md hover:border-blue-200 ${
                                            isSelected ? 'border-blue-300 bg-blue-50/50 shadow-sm' : 'border-gray-100'
                                        } ${isInCart ? 'opacity-50' : ''}`}
                                        data-testid={`product-row-${product.id}`}
                                    >
                                        <div className="flex items-center">
                                            {/* Checkbox */}
                                            <div className="w-8 flex-shrink-0">
                                                <Checkbox 
                                                    checked={isSelected}
                                                    onCheckedChange={() => toggleProductSelection(product.id)}
                                                    disabled={isInCart}
                                                    className="border-slate-300"
                                                />
                                            </div>

                                            {/* Product Info - Grouped (Image + Model + TVA + Color + Storage) */}
                                            <div className="w-[300px] flex-shrink-0 flex items-center gap-3">
                                                {/* Product Image Placeholder with Promo Badge */}
                                                <div className="relative w-11 h-11 flex-shrink-0">
                                                    <div className="w-full h-full bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg flex items-center justify-center border border-slate-100">
                                                        <Smartphone className="w-5 h-5 text-slate-400" />
                                                    </div>
                                                    {/* Promo Badge */}
                                                    {product.promo && (
                                                        <div className="absolute -top-1.5 -right-1.5 z-10">
                                                            <TooltipProvider delayDuration={100}>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full shadow-md flex items-center gap-0.5">
                                                                            <Flame className="w-2.5 h-2.5" />
                                                                            PROMO
                                                                        </div>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent side="top" className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-xl">
                                                                        <div className="text-sm font-semibold">{product.promo.name}</div>
                                                                        {product.promo.pricing_tiers && (
                                                                            <div className="text-xs mt-1 opacity-90">
                                                                                {Object.entries(product.promo.pricing_tiers)
                                                                                    .sort(([a], [b]) => parseInt(a) - parseInt(b))
                                                                                    .map(([qty, price]) => (
                                                                                        <div key={qty}>
                                                                                            {qty === '1' ? 'Prix unitaire' : `≥${qty}pcs`}: {price}€
                                                                                        </div>
                                                                                    ))
                                                                                }
                                                                            </div>
                                                                        )}
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        </div>
                                                    )}
                                                    {/* Custom Product Badge (non-promo) */}
                                                    {product.badge && !product.promo && (
                                                        <div className="absolute -top-1.5 -right-1.5 z-10">
                                                            <span 
                                                                className="text-[8px] font-bold px-1.5 py-0.5 rounded-full shadow-md flex items-center gap-0.5"
                                                                style={{ 
                                                                    backgroundColor: product.badge.bg_color || '#f97316', 
                                                                    color: product.badge.text_color || '#ffffff' 
                                                                }}
                                                            >
                                                                <Tag className="w-2.5 h-2.5" />
                                                                {product.badge.name}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                {/* Model + Details */}
                                                <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                                                    <span className="font-bold text-slate-900 text-sm leading-tight truncate" style={{ fontFamily: 'var(--font-heading)' }}>
                                                        {product.brand} {product.model}
                                                    </span>
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        {/* TVA Badge */}
                                                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                                                            product.vat_regime === 'margin' 
                                                                ? 'bg-blue-100 text-blue-700' 
                                                                : 'bg-gray-100 text-gray-700'
                                                        }`}>
                                                            {product.vat_regime === 'margin' ? 'TVA Marge' : 'TVA Std'}
                                                        </span>
                                                        {/* Color */}
                                                        {product.color && (
                                                            <span className="flex items-center gap-1 text-[11px] text-slate-600">
                                                                <span className={`w-2.5 h-2.5 rounded-full border ${getColorDot(product.color)}`}></span>
                                                                {product.color}
                                                            </span>
                                                        )}
                                                        {/* Storage + Version */}
                                                        {product.storage_capacity && (
                                                            <span className="flex items-center gap-0.5 text-[11px] text-slate-600 font-semibold">
                                                                <HardDrive className="w-3 h-3" />
                                                                {product.storage_capacity}Go
                                                                {product.version && (
                                                                    <span className="text-blue-600 ml-0.5">{product.version}</span>
                                                                )}
                                                            </span>
                                                        )}
                                                        {!product.storage_capacity && product.version && (
                                                            <span className="flex items-center gap-0.5 text-[11px] text-blue-600 font-semibold">
                                                                {product.version}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {/* IMEI small */}
                                                    <span className="text-[9px] text-slate-400 font-mono">{product.imei}</span>
                                                </div>
                                            </div>

                                            {/* Remaining columns - distributed evenly */}
                                            <div className="flex-1 flex items-center">
                                                {/* Grade */}
                                                <div className="text-center min-w-[60px] flex-1">
                                                    {product.grade ? (
                                                        <Badge className={`rounded-full px-2.5 py-0.5 text-xs font-bold border ${getGradeBadge(product.grade)}`}>
                                                            {product.grade}
                                                        </Badge>
                                                    ) : (
                                                        <span className="text-xs text-slate-300">-</span>
                                                    )}
                                                </div>

                                                {/* État (Aesthetic Condition) */}
                                                <div className="text-center min-w-[90px] flex-1">
                                                    {product.aesthetic_condition ? (
                                                        <div className={`flex items-center justify-center gap-1 text-xs font-medium ${
                                                            product.aesthetic_condition === 'Fonctionnel' ? 'text-emerald-600' : 
                                                            product.aesthetic_condition === 'À réparer' ? 'text-amber-600' : 'text-slate-600'
                                                        }`}>
                                                            {product.aesthetic_condition === 'Fonctionnel' ? (
                                                                <CheckCircle className="w-3.5 h-3.5" />
                                                            ) : (
                                                                <Wrench className="w-3.5 h-3.5" />
                                                            )}
                                                            <span>{product.aesthetic_condition}</span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-slate-300">-</span>
                                                    )}
                                                </div>

                                                {/* Lock Status */}
                                                <div className="text-center min-w-[80px] flex-1">
                                                    <div className={`flex items-center justify-center gap-1 text-xs ${lockInfo.className}`}>
                                                        {lockInfo.icon}
                                                        <span>{lockInfo.text}</span>
                                                    </div>
                                                </div>

                                                {/* Cosmetic - Stacked display */}
                                                <div className="text-center min-w-[90px] flex-1">
                                                    {cosmeticInfo.lines.length === 1 && cosmeticInfo.lines[0] === 'OK' ? (
                                                        <div className={`flex items-center justify-center gap-1 text-xs ${cosmeticInfo.className}`}>
                                                            {cosmeticInfo.icon}
                                                            <span>OK</span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col items-center gap-0.5">
                                                            {cosmeticInfo.lines.map((line, idx) => (
                                                                <span key={idx} className={`text-[10px] font-medium ${cosmeticInfo.className} flex items-center gap-1`}>
                                                                    {idx === 0 && cosmeticInfo.icon}
                                                                    {line}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Pannes */}
                                                <div className="text-center min-w-[80px] flex-1">
                                                    <CompactPannesDisplay 
                                                        pannes={product.pannes} 
                                                        productName={`${product.brand} ${product.model}`}
                                                        hasNiveau3={product.has_niveau_3}
                                                    />
                                                </div>

                                                {/* Pièces inconnues */}
                                                <div className="text-center min-w-[80px] flex-1">
                                                    {product.pieces_inconnues && product.pieces_inconnues.length > 0 ? (
                                                        <div className="flex flex-col items-center gap-0.5">
                                                            {product.pieces_inconnues.map((piece, idx) => (
                                                                <span key={idx} className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                                                                    {piece}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="text-emerald-600 text-lg font-bold">✓</span>
                                                    )}
                                                </div>

                                                {/* Batterie */}
                                                <div className="text-center min-w-[70px] flex-1">
                                                    {(() => {
                                                        const batteryInfo = getBatteryDisplay(product.battery_health);
                                                        return (
                                                            <div className={`flex items-center justify-center gap-1 text-xs font-medium ${batteryInfo.color}`}>
                                                                {batteryInfo.icon}
                                                                <span>{batteryInfo.text}</span>
                                                            </div>
                                                        );
                                                    })()}
                                                </div>

                                                {/* Price */}
                                                <div className="text-center min-w-[80px] flex-1">
                                                    <div className="inline-flex items-baseline">
                                                        <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-mono)' }}>
                                                            {priceRounded.toLocaleString('fr-FR')}
                                                        </span>
                                                        <span className="text-sm font-bold text-slate-400 ml-0.5">€</span>
                                                    </div>
                                                </div>

                                                {/* Add to Cart Button */}
                                                <div className="text-center min-w-[50px] flex-1">
                                                    <Button 
                                                        size="sm"
                                                        variant={isInCart ? 'secondary' : 'default'}
                                                        disabled={isInCart || isAdding}
                                                        onClick={() => handleAddToCart(product.id)}
                                                        className={`w-10 h-10 rounded-xl p-0 transition-all ${
                                                            !isInCart 
                                                                ? 'bg-blue-600 text-white hover:scale-105 hover:bg-blue-700 shadow-md' 
                                                                : 'bg-gray-100 text-gray-400'
                                                        }`}
                                                        data-testid={`add-to-cart-${product.id}`}
                                                    >
                                                        {isAdding ? (
                                                            <RefreshCw className="w-4 h-4 animate-spin" />
                                                        ) : isInCart ? (
                                                            <Check className="w-4 h-4" />
                                                        ) : (
                                                            <ShoppingCart className="w-4 h-4" />
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Pagination - Always visible */}
                    <div className="flex justify-center items-center mt-6 mb-8 gap-2">
                        {/* Page Numbers */}
                        <div className="flex items-center gap-1">
                            {/* First page */}
                            <button
                                onClick={() => setPage(1)}
                                className={`min-w-[40px] h-10 px-3 rounded-lg border text-sm font-medium transition-all ${
                                    page === 1 
                                        ? 'bg-blue-600 text-white border-blue-600' 
                                        : 'bg-white text-slate-700 border-slate-200 hover:border-blue-300 hover:bg-blue-50'
                                }`}
                                data-testid="pagination-page-1"
                            >
                                1
                            </button>
                            
                            {/* Second page if exists */}
                            {totalPages > 1 && (
                                <button
                                    onClick={() => setPage(2)}
                                    className={`min-w-[40px] h-10 px-3 rounded-lg border text-sm font-medium transition-all ${
                                        page === 2 
                                            ? 'bg-blue-600 text-white border-blue-600' 
                                            : 'bg-white text-slate-700 border-slate-200 hover:border-blue-300 hover:bg-blue-50'
                                    }`}
                                    data-testid="pagination-page-2"
                                >
                                    2
                                </button>
                            )}
                            
                            {/* Ellipsis if needed */}
                            {totalPages > 3 && page < totalPages - 1 && (
                                <span className="px-2 text-slate-400">...</span>
                            )}
                            
                            {/* Current page if not 1, 2, or last */}
                            {page > 2 && page < totalPages && (
                                <button
                                    className="min-w-[40px] h-10 px-3 rounded-lg border text-sm font-medium bg-blue-600 text-white border-blue-600"
                                    data-testid="pagination-page-current"
                                >
                                    {page}
                                </button>
                            )}
                            
                            {/* Ellipsis before last if needed */}
                            {totalPages > 3 && page > 2 && page < totalPages - 1 && (
                                <span className="px-2 text-slate-400">...</span>
                            )}
                            
                            {/* Next button (arrow) */}
                            {totalPages > 1 && (
                                <button
                                    onClick={() => setPage(p => Math.min(p + 1, totalPages))}
                                    disabled={page === totalPages}
                                    className="min-w-[40px] h-10 px-3 rounded-lg border text-sm font-medium bg-white text-slate-700 border-slate-200 hover:border-blue-300 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="pagination-next"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            )}
                            
                            {/* Last page */}
                            {totalPages > 2 && (
                                <button
                                    onClick={() => setPage(totalPages)}
                                    className={`min-w-[40px] h-10 px-3 rounded-lg border text-sm font-medium transition-all ${
                                        page === totalPages 
                                            ? 'bg-blue-600 text-white border-blue-600' 
                                            : 'bg-white text-slate-700 border-slate-200 hover:border-blue-300 hover:bg-blue-50'
                                    }`}
                                    data-testid="pagination-page-last"
                                >
                                    {totalPages}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                </div>

                {/* Floating "Back to Top" Button */}
                <button
                    onClick={scrollToTop}
                    className={`fixed bottom-8 right-8 z-50 w-12 h-12 bg-blue-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:bg-blue-700 hover:scale-110 ${
                        showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
                    }`}
                    data-testid="scroll-to-top"
                    title="Remonter en haut"
                >
                    <ChevronUp className="w-6 h-6" />
                </button>
            </div>
        </Layout>
    );
};

export default CatalogPage;
